import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, LogIn, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";

const AuthPage = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    password: "",
    confirmPassword: ""
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginData.email || !loginData.password) {
      toast.error("Email ve şifre gerekli");
      return;
    }

    setLoading(true);
    try {
      const result = await login(loginData.email, loginData.password);
      if (result.success) {
        toast.success("Giriş başarılı!");
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Giriş başarısız");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!registerData.name || !registerData.email || !registerData.password) {
      toast.error("Ad, email ve şifre gerekli");
      return;
    }

    if (registerData.password !== registerData.confirmPassword) {
      toast.error("Şifreler eşleşmiyor");
      return;
    }

    if (registerData.password.length < 6) {
      toast.error("Şifre en az 6 karakter olmalı");
      return;
    }

    setLoading(true);
    try {
      const result = await register({
        name: registerData.name,
        email: registerData.email,
        phone: registerData.phone || null,
        company: registerData.company || null,
        password: registerData.password
      });
      
      if (result.success) {
        toast.success("Kayıt başarılı! Hoş geldiniz.");
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kayıt başarısız");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-16 md:pt-20 min-h-screen bg-[#0a0a0f] flex items-center justify-center" data-testid="auth-page">
      <div className="w-full max-w-md mx-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#f0f0f5] mb-2">Hesabınız</h1>
          <p className="text-[#9ca3af]">Giriş yapın veya yeni hesap oluşturun</p>
        </div>

        <div className="bg-[#16161f] border border-[#2a2a3a] rounded-xl p-6">
          <Tabs defaultValue="login">
            <TabsList className="w-full bg-[#0a0a0f] border border-[#2a2a3a] mb-6">
              <TabsTrigger 
                value="login" 
                className="flex-1 data-[state=active]:bg-[#00d4aa] data-[state=active]:text-[#0a0a0f]"
                data-testid="tab-login"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Giriş Yap
              </TabsTrigger>
              <TabsTrigger 
                value="register" 
                className="flex-1 data-[state=active]:bg-[#00d4aa] data-[state=active]:text-[#0a0a0f]"
                data-testid="tab-register"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Kayıt Ol
              </TabsTrigger>
            </TabsList>

            {/* Login Form */}
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    value={loginData.email}
                    onChange={(e) => setLoginData(p => ({ ...p, email: e.target.value }))}
                    placeholder="ornek@email.com"
                    className="bg-[#0a0a0f] border-[#2a2a3a]"
                    data-testid="login-email"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="login-password">Şifre</Label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showPassword ? "text" : "password"}
                      value={loginData.password}
                      onChange={(e) => setLoginData(p => ({ ...p, password: e.target.value }))}
                      placeholder="••••••••"
                      className="bg-[#0a0a0f] border-[#2a2a3a] pr-10"
                      data-testid="login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9ca3af] hover:text-[#f0f0f5]"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#00d4aa] hover:bg-[#00b894] text-[#0a0a0f]"
                  data-testid="login-submit"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Giriş yapılıyor...</>
                  ) : (
                    <><LogIn className="w-4 h-4 mr-2" />Giriş Yap</>
                  )}
                </Button>
              </form>
            </TabsContent>

            {/* Register Form */}
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="register-name">Ad Soyad *</Label>
                  <Input
                    id="register-name"
                    value={registerData.name}
                    onChange={(e) => setRegisterData(p => ({ ...p, name: e.target.value }))}
                    placeholder="Adınız Soyadınız"
                    className="bg-[#0a0a0f] border-[#2a2a3a]"
                    data-testid="register-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="register-email">Email *</Label>
                  <Input
                    id="register-email"
                    type="email"
                    value={registerData.email}
                    onChange={(e) => setRegisterData(p => ({ ...p, email: e.target.value }))}
                    placeholder="ornek@email.com"
                    className="bg-[#0a0a0f] border-[#2a2a3a]"
                    data-testid="register-email"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-phone">Telefon</Label>
                    <Input
                      id="register-phone"
                      value={registerData.phone}
                      onChange={(e) => setRegisterData(p => ({ ...p, phone: e.target.value }))}
                      placeholder="+90 5XX"
                      className="bg-[#0a0a0f] border-[#2a2a3a]"
                      data-testid="register-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-company">Şirket</Label>
                    <Input
                      id="register-company"
                      value={registerData.company}
                      onChange={(e) => setRegisterData(p => ({ ...p, company: e.target.value }))}
                      placeholder="Şirket Adı"
                      className="bg-[#0a0a0f] border-[#2a2a3a]"
                      data-testid="register-company"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="register-password">Şifre *</Label>
                  <div className="relative">
                    <Input
                      id="register-password"
                      type={showPassword ? "text" : "password"}
                      value={registerData.password}
                      onChange={(e) => setRegisterData(p => ({ ...p, password: e.target.value }))}
                      placeholder="En az 6 karakter"
                      className="bg-[#0a0a0f] border-[#2a2a3a] pr-10"
                      data-testid="register-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9ca3af] hover:text-[#f0f0f5]"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="register-confirm">Şifre Tekrar *</Label>
                  <Input
                    id="register-confirm"
                    type="password"
                    value={registerData.confirmPassword}
                    onChange={(e) => setRegisterData(p => ({ ...p, confirmPassword: e.target.value }))}
                    placeholder="Şifreyi tekrar girin"
                    className="bg-[#0a0a0f] border-[#2a2a3a]"
                    data-testid="register-confirm"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#00d4aa] hover:bg-[#00b894] text-[#0a0a0f]"
                  data-testid="register-submit"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Kayıt yapılıyor...</>
                  ) : (
                    <><UserPlus className="w-4 h-4 mr-2" />Kayıt Ol</>
                  )}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>

        <p className="text-center text-[#9ca3af] text-sm mt-6">
          Kayıt olarak{" "}
          <Link to="/iletisim" className="text-[#00d4aa] hover:underline">
            kullanım şartlarını
          </Link>{" "}
          kabul etmiş olursunuz.
        </p>
      </div>
    </div>
  );
};

export default AuthPage;
